import React, { useState, useEffect } from "react";
import Button from "../components/Buttons/Button";
import { Plus, Pencil, Trash, ArrowRight, ArrowLeft } from "lucide-react";
import Card from "../components/Card";
import Swal from "sweetalert2";
import Modal from "../components/models/Modal";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../components/Loader.jsx";
import FaqShimmerCard from "../components/Shimmers/FaqShimmerCard.jsx";
import {
  fetchFaqs,
  addOrUpdateFaq,
  deleteFaq,
  fetchNextFaqPriority,
  statusupdatefaq,
} from "../redux/slices/faqSlice.js";

const Faqpage = () => {
  const dispatch = useDispatch();

  // ---------------- STATES ----------------
  const [activeTab, setActiveTab] = useState("customer");
  const [currentPage, setCurrentPage] = useState(1); // pagination
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [addQuestion, setAddQuestion] = useState("");
  const [addAnswer, setAddAnswer] = useState("");
  const [addCategory, setAddCategory] = useState(""); // for category select
  const [addPriority, setAddPriority] = useState("New"); // or number like 1, 2, 3
  const [faqStatusMap, setFaqStatusMap] = useState({});
  const [initialLoad, setInitialLoad] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  // ---------------- FIXED SELECTOR ----------------
  const faqState = useSelector((state) => state.faqs) || {};
  const {
    list = [],
    loading = false,
    error = null,
    total_pages = 1,
  } = faqState;

  const isFaqLoading = loading && !initialLoad;
  // ------------------------------------------------------------
  // FETCH FAQS FROM API
  // ------------------------------------------------------------
  useEffect(() => {
    const audienceValue = activeTab === "customer" ? 1 : 2;
    dispatch(fetchFaqs({ audience: audienceValue, page: currentPage })).finally(
      () => setInitialLoad(false)
    );
  }, [dispatch, activeTab, currentPage]);

  // ------------------------------------------------------------
  // HANDLE EDIT
  // ------------------------------------------------------------
  const handleEdit = (faq) => {
    setEditData(faq);
    setIsEditModalOpen(true);
  };

  // ------------------------------------------------------------
  // HANDLE DELETE
  // ------------------------------------------------------------
  const handleDelete = (faqId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Delete this FAQ?",
      icon: "warning",
      showDenyButton: true,
      confirmButtonText: "Yes",
      denyButtonText: "No",
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(deleteFaq(faqId))
          .then(() => {
            const audienceValue = activeTab === "customer" ? 1 : 2;

            // If this was the last item on the page and page > 1, go to previous page
            if (list.length === 1 && currentPage > 1) {
              setCurrentPage((prev) => prev - 1);
            } else {
              // Else, just refetch the current page
              dispatch(
                fetchFaqs({ audience: audienceValue, page: currentPage })
              );
            }

            Swal.fire("Deleted!", "", "success");
          })
          .catch((err) => {
            Swal.fire(
              "Error!",
              err.message || "Could not delete FAQ.",
              "error"
            );
          });
      }
    });
  };

  // ------------------------------------------------------------
  // SAVE EDIT
  // ------------------------------------------------------------
  const onSaveEdit = async () => {
    setIsUpdating(true);

    try {
      await dispatch(
        addOrUpdateFaq({
          faq_id: editData.faq_id,
          faq_audience: activeTab === "customer" ? 1 : 2,
          question: editData.question,
          answers: editData.answers,
          priority: editData.priority || 1,
        })
      ).unwrap();

      const audienceValue = activeTab === "customer" ? 1 : 2;

      dispatch(fetchFaqs({ audience: audienceValue, page: currentPage }));

      setIsEditModalOpen(false);
      Swal.fire("Success", "FAQ updated successfully", "success");
    } finally {
      setIsUpdating(false);
    }
  };

  // ------------------------------------------------------------
  // SAVE ADD
  // ------------------------------------------------------------

  const onSaveAdd = async () => {
    if (!addQuestion || !addAnswer || !addCategory) {
      Swal.fire("Error", "Please fill all fields", "error");
      return;
    }

    setIsAdding(true); // START SPINNER

    const audienceValue = Number(addCategory);

    try {
      const nextPriority = await dispatch(
        fetchNextFaqPriority(audienceValue)
      ).unwrap();

      const payload = {
        faq_audience: audienceValue,
        question: addQuestion.trim(),
        answers: addAnswer.trim(),
        priority: nextPriority,
      };

      await dispatch(addOrUpdateFaq(payload)).unwrap();

      Swal.fire("Success", "FAQ added successfully", "success");

      setAddQuestion("");
      setAddAnswer("");
      setAddCategory("");
      setAddPriority("1");
      setIsAddModalOpen(false);

      dispatch(fetchFaqs({ audience: audienceValue, page: currentPage }));
    } catch (error) {
      Swal.fire("Error", error?.message || "Something went wrong", "error");
    } finally {
      setIsAdding(false); // STOP SPINNER
    }
  };

  const isAddDisabled = !addQuestion.trim() || !addAnswer.trim();

  // ------------------------------------------------------------
  // PAGINATION HANDLERS
  // ------------------------------------------------------------
  const handlePrevious = () => {
    if (!isFaqLoading && currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };
  const handleNext = () => {
    if (!isFaqLoading && currentPage < total_pages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePageClick = (page) => {
    if (!isFaqLoading) setCurrentPage(page);
  };

  // HANDLE STATUS
  const handleStatusToggle = (faq) => {
    const currentStatus = faqStatusMap[faq.faq_id] ?? faq.status; // 1 or 0
    const action = currentStatus === 1 ? "deactivate" : "activate";

    Swal.fire({
      title: "Are you sure?",
      text: `Do you want to ${action} this FAQ?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    }).then((result) => {
      if (result.isConfirmed) {
        const newStatus = currentStatus === 1 ? 0 : 1; // flip numeric

        dispatch(
          statusupdatefaq({
            faq_id: faq.faq_id,
            is_confirmed: newStatus,
          })
        )
          .unwrap()
          .then((res) => {
            // Update state only after API success
            setFaqStatusMap((prev) => ({
              ...prev,
              [faq.faq_id]: newStatus,
            }));

            Swal.fire(
              "Updated!",
              "FAQ status updated successfully.",
              "success"
            );
          })
          .catch(() => {
            Swal.fire("Error!", "Could not update FAQ status.", "error");
          });
      }
      // if user cancels, do nothing
    });
  };

  if (initialLoad) return <Loader />;

  return (
    <>
      <section>
        <div className="flex flex-col sm:flex-row justify-between">
          <div>
            <h1 className="text-2xl font-semibold mb-4">Manage FAQs</h1>
            <p className="text-sm text-gray-500 mt-1 mb-2">
              Add, edit or delete frequently asked questions.
            </p>
          </div>

          <Button
            classes="mt-4 w-full sm:w-[200px]"
            onClick={() => setIsAddModalOpen(true)}
            text={"Add New FAQ"}
            icon={<Plus size={20} />}
            iconPosition="left"
          />
        </div>

        {/* ---------------- TABS ---------------- */}
        <div className="flex gap-10 mt-8 border-b pb-3">
          <button
            onClick={() => {
              if (activeTab !== "customer") {
                setInitialLoad(false);
                setActiveTab("customer");
                setCurrentPage(1);
              }
            }}
            className={`pb-2 text-sm font-medium cursor-pointer ${
              activeTab === "customer"
                ? "text-[#FF6943] border-b-2 border-[#FF6943]"
                : "text-gray-500"
            }`}
          >
            Customer FAQs
          </button>



          <button
            onClick={() => {
              if (activeTab !== "provider") {
                  setInitialLoad(false); 
                setActiveTab("provider");
                setCurrentPage(1);
              }
            }}
            className={`pb-2 text-sm font-medium cursor-pointer ${
              activeTab === "provider"
                ? "text-[#FF6943] border-b-2 border-[#FF6943]"
                : "text-gray-500"
            }`}
          >
            Provider FAQs
          </button>
        </div>

        {/* ---------------- FAQ LIST ---------------- */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mt-8">
          {/* SHOW SHIMMER IN PLACE OF CARDS */}
          {isFaqLoading ? (
            <>
              {Array.from({ length: 6 }).map((_, i) => (
                <FaqShimmerCard key={i} />
              ))}
            </>
          ) : (
            <>
              {list.map((faq, index) => (
                <Card
                  key={faq.faq_id || index}
                  cardWidth="100%"
                  cardTitle={faq.question}
                  cardContent={faq.answers}
                  cardIconOne={<Pencil size={20} />}
                  cardIconTwo={<Trash size={20} />}
                  onclickOne={() => handleEdit(faq)}
                  onClickTwo={() => handleDelete(faq.faq_id)}
                  checked={faqStatusMap[faq.faq_id] ?? faq.status === 1}
                  onToggle={() => handleStatusToggle(faq)}
                  status={
                    (faqStatusMap[faq.faq_id] ?? faq.status) === 1
                      ? "Active"
                      : "Inactive"
                  }
                />
              ))}
            </>
          )}
        </div>

        {/* ---------------- PAGINATION ---------------- */}
        {!isFaqLoading && (
          <div className="flex  flex-row justify-center items-center mt-5 gap-2">
            <button
              onClick={handlePrevious}
              disabled={currentPage === 1}
              className="px-3 py-1 border rounded disabled:bg-gray-200 disabled:cursor-not-allowed flex items-center gap-1"
            >
              <ArrowLeft className="w-4 h-4" /> Previous
            </button>

            <div className="flex space-x-1">
              {Array.from({ length: total_pages }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => handlePageClick(i + 1)}
                  className={`px-3 py-1 rounded text-sm ${
                    currentPage === i + 1
                      ? "bg-orange-500 text-white"
                      : "bg-gray-100 hover:bg-gray-200"
                  }`}
                >
                  {i + 1}
                </button>
              ))}
            </div>

            <button
              onClick={handleNext}
              disabled={currentPage === total_pages}
              className="px-3 py-1 border rounded disabled:bg-gray-200 disabled:cursor-not-allowed flex items-center gap-1"
            >
              Next <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </section>

      {/* ===================================== EDIT MODAL ===================================== */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit FAQ"
        footer={
          <div className="flex gap-4">
            <button
              className="bg-green-500 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2"
              onClick={onSaveEdit}
              disabled={isUpdating}
            >
              {isUpdating ? (
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              ) : (
                "Update"
              )}
            </button>

            <button
              className="bg-red-500 text-white px-4 py-2 rounded-lg"
              onClick={() => setIsEditModalOpen(false)}
            >
              Cancel
            </button>
          </div>
        }
      >
        {editData && (
          <form className="space-y-5">
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">
                Category
              </label>

              <select className="appearance-none border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 bg-gray-50">
                <option>Tiffin Provider FAQ</option>
                <option>Customers FAQ</option>
              </select>
            </div>

            <div className="flex flex-col gap-1">
              <label
                className="text-sm font-medium text-gray-700"
                htmlFor="editfaqquestion"
              >
                Question
              </label>

              <input
                type="text"
                name="editfaqquestion"
                className="border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 bg-gray-50"
                value={editData.question}
                onChange={(e) =>
                  setEditData({ ...editData, question: e.target.value })
                }
              />
            </div>

            <div className="flex flex-col gap-1">
              <label
                className="text-sm font-medium text-gray-700"
                htmlFor="editfaqanswer"
              >
                Answer
              </label>

              <textarea
                name="editfaqanswer"
                rows={4}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 bg-gray-50"
                value={editData.answers}
                onChange={
                  (e) => setEditData({ ...editData, answers: e.target.value }) // <-- fix here
                }
              />
            </div>
          </form>
        )}
      </Modal>

      {/* ===================================== ADD MODAL ===================================== */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        title="Add FAQ"
        footer={
          <div className="flex gap-4">
            <button
              onClick={onSaveAdd}
              disabled={isAddDisabled || isAdding}
              className={`px-4 py-2 rounded-lg flex items-center justify-center gap-2 ${
                isAddDisabled || isAdding
                  ? "bg-gray-300 text-gray-500"
                  : "bg-green-500 text-white"
              }`}
            >
              {isAdding ? (
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              ) : (
                "Save"
              )}
            </button>

            <button
              onClick={() => setIsAddModalOpen(false)}
              className="bg-red-500 text-white px-4 py-2 rounded-lg"
            >
              Cancel
            </button>
          </div>
        }
      >
        <form className="space-y-5">
          {/* <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">
              Priority
            </label>

            <select
              value={addPriority}
              onChange={(e) => setAddPriority(e.target.value)}
              className="appearance-none border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 bg-gray-50"
            >
              <option value="14">High</option>
            </select>
          </div> */}

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">
              Category
            </label>

            <select
              id="selectaddcategory"
              value={addCategory}
              onChange={(e) => setAddCategory(e.target.value)}
              className="appearance-none border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 bg-gray-50"
            >
              <option value="">Select Category</option>
              <option value="1"> Customers FAQ</option>
              <option value="2"> Tiffin Provider FAQ</option>
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label
              className="text-sm font-medium text-gray-700"
              htmlFor="addfaqquestion"
            >
              Question
            </label>
            <input
              type="text"
              placeholder="Question"
              className="border p-2 rounded w-full"
              value={addQuestion}
              onChange={(e) => setAddQuestion(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label
              className="text-sm font-medium text-gray-700"
              htmlFor="addfaqanswer"
            >
              Answer
            </label>

            <textarea
              rows={4}
              placeholder="Answer"
              className="border p-2 rounded w-full"
              value={addAnswer}
              onChange={(e) => setAddAnswer(e.target.value)}
            ></textarea>
          </div>
        </form>
      </Modal>
    </>
  );
};

export default Faqpage;
